import { Injectable, inject } from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {
  courseLoaded,
  updateCourseSortOrderCompleted, updateCourseSyncClones,
  updateCourseThumbnailSyncClone,
  userPrivateLoaded
} from "../store/course.actions";
import {catchError, concatMap, exhaustMap, filter, first, map, switchMap, take, tap, withLatestFrom} from 'rxjs/operators';
import {CoursesDBService} from '../services/courses-db.service';
import {combineLatest, EMPTY, of, throwError} from 'rxjs';
import {AppState} from './index';
import {select, Store} from '@ngrx/store';
import {loadDescription} from './description.actions';
import {
  isActiveCourseSectionsLoaded, selectActiveCourse,
  selectPendingCoursesReorder,
  selectPendingSectionsReorder,
  selectUser,
  selectLessonsState
} from './selectors';
import {LessonsDBService} from '../services/lessons-db.service';
import {courseLessonsLoaded} from './lesson.actions';
import {SchoolUsersDbService} from '../services/school-users-db.service';
import {TenantService} from '../services/tenant.service';
import {CourseActions, CourseSectionActions} from './action-types';
import {courseSectionsLoaded, updateSectionOrderCompleted, courseSectionPageLoaded} from './course-section.actions';
import {ProtectedResourceService} from '../services/protected-resource.service';
import {MessagesService} from '../shared/services/messages.service';
import {LoadingService} from '../shared/services/loading.service';
import {loadAllAuthors} from './authors.actions';
import { UserActivityLogService } from "../services/user-activity-log.service";
import { Auth, authState } from '@angular/fire/auth';
import {environment} from "../../environments/environment";
import { StudentsService } from '../admin/manage-students/students.service';
import { CourseDeleteService } from '../services/course-delete.service';


@Injectable()
export class CourseEffects {
  private actions$ = inject(Actions);
  private coursesDB = inject(CoursesDBService);
  private lessonsDB = inject(LessonsDBService);
  private store = inject<Store<AppState>>(Store);
  private loading = inject(LoadingService);
  private messages = inject(MessagesService);
  private afAuth = inject(Auth);
  private usersDB = inject(SchoolUsersDbService);
  private tenant = inject(TenantService);
  private resources = inject(ProtectedResourceService);
  private userActivityLogService = inject(UserActivityLogService);
  private studentsService = inject(StudentsService);
  private courseDeleteService = inject(CourseDeleteService);


  createNewCourse$ = createEffect(() => this.actions$
      .pipe(
        ofType(CourseActions.createNewCourse),
        concatMap(({course}) => this.coursesDB.createNewCourse(course))
      ),
    {dispatch: false});

  loadUserPrivate$ = createEffect(() => combineLatest(
    authState(this.afAuth),
    this.tenant.tenantId$
  )
    .pipe(
      filter(([user, tenantId]) => !!(user?.uid && tenantId)),
      tap(data => console.log("loadUserPrivate$ effect called ...", data)),
      concatMap(([user, tenantId]) => this.usersDB.loadUserPrivate(tenantId, user.uid)),
      map(userPrivate => userPrivateLoaded({...userPrivate})),
      catchError(err => {
        console.log('Could not load user private data on loadUserPrivate$ effect...');
        return throwError(err);
      })
    ));

  reloadUserBundles$ = createEffect(() => this.actions$.pipe(
    ofType(CourseActions.bundlePurchased),
    withLatestFrom(
      this.tenant.tenantId$,
      this.store.pipe(select(selectUser)),
    ),
    tap(console.log),
    concatMap(([bundleId, tenantId, user]) => this.usersDB.loadUserPrivate(tenantId, user.id)),
    map(userPrivate => userPrivateLoaded({...userPrivate})),
    catchError(err => {
      this.messages.error('Could not load user bundles.');
      return throwError(err);
    })
  ));

  loadCourseDescriptionIfNeeded$ = createEffect(() => this.actions$
    .pipe(
      ofType(CourseActions.loadCourseDetail),
      map(({courseId}) => loadDescription({descriptionId: courseId})),
      catchError(err => {
        this.messages.error('Could not load course description.');
        return throwError(err);
      })
    ));


  loadFirstSectionsPage$ = createEffect(() => this.actions$.pipe(
    ofType(CourseActions.loadCourseDetail),
    withLatestFrom(this.store.pipe(select(isActiveCourseSectionsLoaded))),
    filter(([action, loaded]) => !action.isWatchCourseRoute && (action.isAdminRoute || !loaded)),
    exhaustMap(([{ courseId, course, isAdminRoute }]) => {
      const targetId = course?.cloneType === 'read-only' && course?.sourceCourseId
        ? course.sourceCourseId : courseId;
      // Admin/edit routes need ALL sections — use full load. Landing page paginates to 3.
      const load$ = isAdminRoute
        ? this.lessonsDB.loadCourseSections(targetId)
        : this.lessonsDB.loadCourseSectionsPage(targetId, 0, 4);
      return this.loading.showLoader(
        load$.pipe(
          map(sections => courseSectionPageLoaded({
            // Admin: store ALL sections. Landing page: store first 3 (4th was lookahead only)
            courseSections: (isAdminRoute ? sections : sections.slice(0, 3)).map(s => ({ ...s, courseId: targetId })),
            courseId,
            allLoaded: isAdminRoute ? true : sections.length < 4,
            direction: 'landing'
          })),
          catchError(err => {
            this.messages.error('Could not load sections.');
            return EMPTY;
          })
        )
      );
    })
  ));

  loadLessonsForNewSections$ = createEffect(() => this.actions$.pipe(
    ofType(CourseSectionActions.courseSectionPageLoaded),
    filter(({ courseSections }) => courseSections.length > 0),
    concatMap(({ courseSections, courseId }) =>
      this.store.pipe(select(selectLessonsState), first()).pipe(
        concatMap(lessonsState => {
          const newSectionIds = courseSections
            .map(s => s.id)
            .filter(id => !lessonsState.loadedSections[id]);
          if (newSectionIds.length === 0) return EMPTY;
          const targetCourseId = courseSections[0]?.courseId ?? courseId;
          return this.lessonsDB.loadLessonsForSections(targetCourseId, newSectionIds).pipe(
            map(lessons => courseLessonsLoaded({ lessons, courseId, sectionIds: newSectionIds })),
            catchError(err => {
              this.messages.error('Could not load lessons.');
              return EMPTY;
            })
          );
        })
      )
    )
  ));

  loadMoreSections$ = createEffect(() => this.actions$.pipe(
    ofType(CourseSectionActions.loadCourseSectionsPage, CourseSectionActions.loadCourseSectionsBefore),
    withLatestFrom(this.store.pipe(select(selectActiveCourse))),
    exhaustMap(([action, course]) => {
      const targetId = course?.cloneType === 'read-only' && course?.sourceCourseId
        ? course.sourceCourseId : action.courseId;
      const isNext = action.type === CourseSectionActions.loadCourseSectionsPage.type;
      // Fetch 2 when going next — if 2 come back there are more; if 1 comes back it's the last one.
      // This avoids an extra empty Firestore read after the final section loads.
      const load$ = isNext
        ? this.lessonsDB.loadCourseSectionsPage(targetId, (action as ReturnType<typeof CourseSectionActions.loadCourseSectionsPage>).afterSeqNo, 2)
        : this.lessonsDB.loadCourseSectionsBefore(targetId, (action as ReturnType<typeof CourseSectionActions.loadCourseSectionsBefore>).beforeSeqNo, 1);
      return load$.pipe(
          map(sections => courseSectionPageLoaded({
            // Only add the first section — second was a lookahead to detect if more exist
            courseSections: sections.slice(0, 1).map(s => ({ ...s, courseId: targetId })),
            courseId: action.courseId,
            allLoaded: isNext ? sections.length < 2 : false,
            direction: isNext ? 'next' : 'prev'
          })),
          catchError(err => {
            this.messages.error('Could not load section.');
            return of(CourseSectionActions.courseSectionPageLoadFailed({ direction: isNext ? 'next' : 'prev' }));
          })
      );
    })
  ));

  loadActiveCourseSection$ = createEffect(() => this.actions$.pipe(
    ofType(CourseActions.loadActiveCourseSection),
    withLatestFrom(this.store.pipe(select(selectActiveCourse))),
    switchMap(([{ courseId, sectionSeqNo }, course]) => {
      const targetId = course?.cloneType === 'read-only' && course?.sourceCourseId
        ? course.sourceCourseId : courseId;
      return this.lessonsDB.loadSectionBySeqNo(targetId, sectionSeqNo).pipe(
        map(sections => courseSectionPageLoaded({
          courseSections: sections.map(s => ({ ...s, courseId: targetId })),
          courseId,
          allLoaded: false,
          direction: 'initial'
        })),
        catchError(err => {
          this.messages.error('Could not load section.');
          return EMPTY;
        })
      );
    })
  ));

  loadAuthorsIfNeeded$ = createEffect(() => this.actions$
    .pipe(
      ofType(CourseActions.loadCourseDetail),
      map(() => loadAllAuthors()),
      catchError(err => {
        this.messages.error('Could not load authors.');
        return throwError(err);
      })
    ));

  deleteCourse$ = createEffect(() => this.actions$
    .pipe(
      ofType(CourseActions.deleteCourse),
      concatMap(({ id }) =>
        this.coursesDB.findCourseByTenantAndId(this.tenant.id, id).pipe(
          first(),
          switchMap((course) => {
            if (course?.type === 'scorm') {
              return this.loading.showLoader(this.courseDeleteService.deleteScormCourseWithCleanup(id));
            } else {
              return this.loading.showLoader(this.courseDeleteService.deleteCourseWithCleanup(id));
            }
          })
        )
      ),
      catchError(err => {
        this.messages.error('Could not delete the course.', err);
        return throwError(err);
      })
    ), { dispatch: false });


  saveCourse$ = createEffect(() => this.actions$
      .pipe(
        ofType(CourseActions.updateCourse),
        withLatestFrom(
          this.store.pipe(select(selectActiveCourse)),
        ),
        concatMap(([{course}, activeCourse]) => {

          const obs$ =  this.loading.showLoader(this.coursesDB.saveCourse(course.id, course.changes))

          if(environment?.features?.cloneCourse) {

            this.store.dispatch(updateCourseSyncClones({course}))

            if(course?.changes?.thumbnail && course?.changes?.thumbnail?.trim()?.length > 0) {
              this.store.dispatch(updateCourseThumbnailSyncClone({course}))
            }

          }

          return obs$.pipe(
            catchError(err => {
              this.messages.error('Could not save course.');
              return EMPTY;
            })
          );

        })
      ),
    {dispatch: false});


  saveCoursesReordering$ = createEffect(() => this.actions$
    .pipe(
      ofType(CourseActions.updateCourseSortOrder),
      withLatestFrom(this.store.pipe(select(selectPendingCoursesReorder))),
      concatMap(([action, changes]) => this.coursesDB.updateCourses(changes)),
      map(() => updateCourseSortOrderCompleted()),
      catchError(err => {
        this.messages.error('Could not save the new course order.');
        return throwError(err);
      })
    ));

  saveCourseSectionReordering$ = createEffect(() => this.actions$
    .pipe(
      ofType(CourseSectionActions.updateSectionOrder),
      withLatestFrom(this.store.pipe(select(selectPendingSectionsReorder))),
      concatMap(([{courseId}, changes]) => this.coursesDB.updateCourseSections(courseId, changes)),
      map(() => updateSectionOrderCompleted()),
      catchError(err => {
        this.messages.error('Could not save the new section order.');
        return throwError(err);
      })
    ));

  publishCourse$ = createEffect(() => this.actions$
    .pipe(
      ofType(CourseActions.coursePublished),
      concatMap(action => this.coursesDB.saveCourse(action.courseId, {status: 'published', url: action.url})),
      catchError(err => {
        this.messages.error('Error publishing course.');
        return throwError(err);
      })
    ), {dispatch: false});

  unpublishCourse$ = createEffect(() => this.actions$
      .pipe(
        ofType(CourseActions.courseUnpublished),
        concatMap(action => this.coursesDB.saveCourse(action.courseId, {status: 'draft'})),
        catchError(err => {
          this.messages.error('Error unpublishing course.');
          return throwError(err);
        })
      ),
    {dispatch: false});

  reportCourseEnrollmentWhenCoursePurchased$ = createEffect(() => this.actions$
    .pipe(
      ofType(CourseActions.coursePurchased),
      withLatestFrom(this.store.pipe(select(selectUser))),
      concatMap(async ([action, user]) => {
          console.log(`Reporting COURSE PURCHASED for user ${user.id} and course ${action.courseId}`);
        // await this.studentsService.createStudentEnrollment(user.id, action.courseId);
        return await this.userActivityLogService.reportCourseEnrollment(user.id,action.courseId);
      }),
      catchError(err => {
        this.messages.error('Error purchasing course.');
        return throwError(err);

      } )
    ), {dispatch: false});

  loadSourceReadOnlyCloneCourse$ = createEffect(() => this.actions$
    .pipe(
      ofType(CourseActions.courseLoaded),
      concatMap(action => {
        if (action.course?.cloneType === 'read-only') {
          return this.coursesDB.findCourseByTenantAndId(this.tenant.id, action.course.sourceCourseId).pipe(take(1))
        }
        return EMPTY
      }),
      tap((course) => {
        if (course) {
          this.store.dispatch(courseLoaded({course}))
        }
      }),
      catchError(err => {
        this.messages.error('Error loading original source course.');
        return throwError(() => err);
      }),
    ), { dispatch: false });

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);


  constructor() {

  }


}
